import React, { useState } from 'react';
import PropTypes from "prop-types";
import { useRouteMatch } from 'react-router';
import { EuiSpacer, EuiErrorBoundary, EuiButton, EuiButtonEmpty, EuiFlyout, EuiPageContentBody, EuiPanel, useIsWithinBreakpoints } from '../../../../src';
import { slugify } from '../../../../src/services/string/slugify'; // @ts-ignore Not TS yet

import playgroundService from '../../services/playground/playground';
import { GuideSectionExample } from './guide_section_parts/guide_section_example';
import { GuideSectionExampleText } from './guide_section_parts/guide_section_text';
import { GuideSectionExampleTabs } from './guide_section_parts/guide_section_tabs';
import classNames from 'classnames';
export var GuideSectionCodeTypesMap = {
  JS: {
    name: 'demoJS',
    displayName: 'Demo JS'
  },
  TSX: {
    name: 'demoTSX',
    displayName: 'Demo TS'
  },
  STRING_JS: {
    name: 'demoJS',
    displayName: 'Demo JS'
  },
  SNIPPET: {
    name: 'snippet',
    displayName: 'Snippet'
  },
  SASS: {
    name: 'sass',
    displayName: 'Sass'
  },
  PROPS: {
    name: 'props',
    displayName: 'Props'
  }
};
export var GuideSection = ({
  id,
  title,
  text,
  demo,
  fullScreen,
  source = [],
  props = {},
  playground,
  ghostBackground,
  wrapText = true,
  demoPanelProps,
  snippet,
  color,
  children,
  className
}) => {
  const {
    path
  } = useRouteMatch();
  const isLargeBreakpoint = useIsWithinBreakpoints(['m', 'l', 'xl']);
  const [renderingPlayground, setRenderingPlayground] = useState(false);

  const renderTabs = () => {
    const hasSnippet = !!snippet; // Don't duplicate in case this function is run multiple times

    if (hasSnippet && !source.find(tab => tab.name === 'snippet')) {
      source.push({ ...GuideSectionCodeTypesMap.SNIPPET,
        snippets: snippet,
        displayName: `${GuideSectionCodeTypesMap.SNIPPET.displayName}${Array.isArray(snippet) ? 's' : ''}`
      });
    }

    const hasPropsTabAlready = source.find(tab => tab.name === 'props');

    if (Object.keys(props).length && !hasPropsTabAlready // Don't duplicate in case this function is run multiple times
    ) {
      source.push({ ...GuideSectionCodeTypesMap.PROPS,
        props: props
      });
    }

    const tabs = [];

    if (source) {
      source.map(source => {
        tabs.push({ // @ts-ignore Complicated
          ...GuideSectionCodeTypesMap[source.type],
          // Make sure the `name` is unique in case there are multiple source languages
          name: source.displayName ? slugify(source.displayName) : // @ts-ignore Complicated
          GuideSectionCodeTypesMap[source.type].name,
          ...source
        });
      });
    }

    return tabs.length ? <GuideSectionExampleTabs tabs={tabs} rightSideControl={renderPlaygroundToggle()} /> : undefined;
  };

  const renderPlaygroundToggle = () => {
    const isPlaygroundUnsupported = typeof window !== 'undefined' && typeof document !== 'undefined' && !!window.MSInputMethodContext && // @ts-ignore doesn't exist?
    !!document.documentMode;

    if (!isPlaygroundUnsupported && !!playground) {
      return <EuiButtonEmpty size="xs" iconType="controlsHorizontal" onClick={() => {
        setRenderingPlayground(rendering => !rendering);
      }}>
          Playground
        </EuiButtonEmpty>;
    }
  };

  const renderPlayground = () => {
    const {
      config,
      setGhostBackground,
      playgroundClassName,
      playgroundPanelProps
    } = playground();
    return playgroundService({
      config,
      setGhostBackground,
      playgroundClassName,
      playgroundPanelProps,
      playgroundToggle: renderPlaygroundToggle(),
      tabs: renderTabs()
    });
  };

  return <EuiPanel id={id} className={classNames('guideSection', className)} color={!isLargeBreakpoint ? 'transparent' : color || 'transparent'} borderRadius="none" paddingSize="l" grow={false}>
      <EuiSpacer size={(color || title) && isLargeBreakpoint ? 'xxl' : 'xs'} />
      <EuiPageContentBody restrictWidth>
        <GuideSectionExampleText title={title} wrapText={wrapText}>
          {text}
        </GuideSectionExampleText>

        {renderingPlayground && <EuiFlyout onClose={() => setRenderingPlayground(false)} size="l" paddingSize="none" closeButtonPosition="outside">
            {renderPlayground()}
          </EuiFlyout>}
        {(demo || fullScreen) && <>
            {text && <EuiSpacer />}
            <GuideSectionExample example={<EuiErrorBoundary>
                  {
            /* eslint-disable-next-line no-nested-ternary */
          }
                  {fullScreen == null ? <div>{demo}</div> : demo == null ? <EuiButton fill iconType="fullScreen" href={`#${path}/${fullScreen.slug}`}>
                      Fullscreen demo
                    </EuiButton> : demo}
                </EuiErrorBoundary>} tabs={renderTabs()} ghostBackground={ghostBackground} demoPanelProps={demoPanelProps} />
          </>}

        {children}
        <EuiSpacer size={color && isLargeBreakpoint ? 'xxl' : 'xs'} />
      </EuiPageContentBody>
    </EuiPanel>;
};
GuideSection.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.node,
  source: PropTypes.arrayOf(PropTypes.any.isRequired),
  demo: PropTypes.node,
  fullScreen: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    demo: PropTypes.node.isRequired
  }),
  demoPanelProps: PropTypes.shape({
    /**
       * Padding for all four sides
       */
    paddingSize: PropTypes.any,

    /**
       * When true the panel will grow in height to match `EuiFlexItem`
       */
    grow: PropTypes.bool,
    panelRef: PropTypes.any,

    /**
       * Background color of the panel;
       * Usually a lightened form of the brand colors
       */
    color: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }),
  props: PropTypes.any,
  playground: PropTypes.any,
  ghostBackground: PropTypes.bool,
  wrapText: PropTypes.bool,
  snippet: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.arrayOf(PropTypes.string.isRequired).isRequired]),
  color: PropTypes.any,
  children: PropTypes.node
};

try {
  GuideSection.__docgenInfo = {
    tags: {},
    filePath: '/app/src-docs/src/components/guide_section/guide_section.tsx',
    description: '',
    displayName: 'GuideSection',
    methods: [],
    props: {
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      title: {
        defaultValue: null,
        description: '',
        name: 'title',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      text: {
        defaultValue: null,
        description: '',
        name: 'text',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      source: {
        defaultValue: {
          value: '[]'
        },
        description: '',
        name: 'source',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'any[]'
        }
      },
      demo: {
        defaultValue: null,
        description: '',
        name: 'demo',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      fullScreen: {
        defaultValue: null,
        description: '',
        name: 'fullScreen',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: '{ slug: string; demo: ReactNode; }'
        }
      },
      demoPanelProps: {
        defaultValue: null,
        description: '',
        name: 'demoPanelProps',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'HTMLAttributes<HTMLDivElement> & Omit<_EuiPanelProps, "hasBorder" | "hasShadow" | "borderRadius">'
        }
      },
      props: {
        defaultValue: {
          value: '{}'
        },
        description: '',
        name: 'props',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'object'
        }
      },
      playground: {
        defaultValue: null,
        description: '',
        name: 'playground',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      ghostBackground: {
        defaultValue: null,
        description: '',
        name: 'ghostBackground',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      wrapText: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'wrapText',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      snippet: {
        defaultValue: null,
        description: '',
        name: 'snippet',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'string | string[]'
        }
      },
      color: {
        defaultValue: null,
        description: '',
        name: 'color',
        parent: {
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        },
        declarations: [{
          fileName: 'app/src-docs/src/components/guide_section/guide_section.tsx',
          name: 'GuideSection'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"subdued" | "success" | "accent" | "danger" | "warning" | "transparent" | "primary" | "plain"',
          value: [{
            value: '"subdued"'
          }, {
            value: '"success"'
          }, {
            value: '"accent"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"transparent"'
          }, {
            value: '"primary"'
          }, {
            value: '"plain"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['GuideSection', 'CommonProps']
  };
} catch (e) {}